import { useSourceReliabilityTable } from '../../../../../hooks/useDataQuality'
import type { TimeWindow } from '../../../../../types/dataQuality'
import ErrorDisplay from '../../../../common/ErrorDisplay'
import LoadingSkeleton from '../../../../common/LoadingSkeleton'
import './CompletenessTable.css'

interface CompletenessTableProps {
  timeWindow: TimeWindow
}

const CompletenessTable = ({ timeWindow }: CompletenessTableProps) => {
  const { data, isLoading, error } = useSourceReliabilityTable({
    time_window: timeWindow,
    limit: 100,
  })

  if (isLoading) {
    return (
      <div className="completeness-dashboard">
        <div className="section-header-compact">
          <h3 className="section-title-compact">Completeness Score</h3>
        </div>
        <LoadingSkeleton variant="table" count={1} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="completeness-dashboard">
        <ErrorDisplay
          error={error.message}
          onRetry={() => window.location.reload()}
          title="Failed to Load Completeness Data"
        />
      </div>
    )
  }

  if (!data || !data.sources || data.sources.length === 0) {
    return (
      <div className="completeness-dashboard">
        <div className="section-header-compact">
          <h3 className="section-title-compact">Completeness Score</h3>
          <p className="section-subtitle-compact">
            Required fields populated per source
          </p>
        </div>
        <p className="no-data-message">No source data available</p>
      </div>
    )
  }

  const getCompletenessColor = (score: number): string => {
    if (score >= 95) return '#00C950'
    if (score >= 90) return '#F0B100'
    return '#FB2C36'
  }

  const getCompletenessClass = (score: number): string => {
    if (score >= 95) return 'high'
    if (score >= 90) return 'medium'
    return 'low'
  }

  return (
    <div className="completeness-dashboard">
      <div className="completeness-header">
        <div className="section-header-compact">
          <h3 className="section-title-compact">Completeness Score</h3>
          <p className="section-subtitle-compact">
            Required fields populated per source
          </p>
        </div>
        <div className="completeness-legend">
          <div className="completeness-legend-item">
            <div className="completeness-legend-dot high"></div>
            <span className="completeness-legend-label">&gt; 95%</span>
          </div>
          <div className="completeness-legend-item">
            <div className="completeness-legend-dot medium"></div>
            <span className="completeness-legend-label">90-95%</span>
          </div>
          <div className="completeness-legend-item">
            <div className="completeness-legend-dot low"></div>
            <span className="completeness-legend-label">&lt; 90%</span>
          </div>
        </div>
      </div>

      <div className="completeness-sources-list">
        {data.sources.filter(source => source.completeness > 0).length > 0 ? (
          data.sources
            .filter(source => source.completeness > 0)
            .map((source, index) => (
              <div
                key={source.source_id || index}
                className="completeness-source-item"
              >
                <div className="completeness-source-header">
                  <span className="completeness-source-name">
                    {source.source_name}
                  </span>
                  <span
                    className={`completeness-score-value ${getCompletenessClass(source.completeness)}`}
                  >
                    {source.completeness.toFixed(1)}%
                  </span>
                </div>
                <div className="completeness-progress-container">
                  <div className="completeness-progress-bar">
                    <div
                      className={`completeness-progress-fill ${getCompletenessClass(source.completeness)}`}
                      style={{
                        width: `${source.completeness}%`,
                        backgroundColor: getCompletenessColor(source.completeness),
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))
        ) : (
          <p className="no-data-message">No sources with completeness data available</p>
        )}
      </div>

      {data.summary && (
        <div className="completeness-summary">
          <div className="completeness-summary-item">
            <span className="completeness-summary-label">Average</span>
            <span className="completeness-summary-value">
              {data.summary.avg_completeness.toFixed(1)}%
            </span>
          </div>
          <div className="completeness-summary-item">
            <span className="completeness-summary-label">Total Sources</span>
            <span className="completeness-summary-value">
              {data.summary.total_sources}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default CompletenessTable
